package com.gloopgo.game.network

import com.gloopgo.game.engine.EjectedMass
import com.gloopgo.game.engine.FoodPellet
import com.gloopgo.game.engine.GloopPlayer
import com.gloopgo.game.engine.LeaderboardEntry
import com.gloopgo.game.moderation.requireValidPlayerReport
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.map
import kotlin.time.Duration.Companion.milliseconds
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Local in-memory implementation of MultiplayerRepository for offline mode,
 * desktop JVM fallback, and unit testing.
 */
class LocalMultiplayerRepository : MultiplayerRepository {

    private var currentUserId: String? = null
    private val _playersFlow = MutableStateFlow<List<PlayerNetworkState>>(emptyList())
    private val _pelletsFlow = MutableStateFlow<List<PelletNetworkState>>(emptyList())
    private val _ejectedFlow = MutableStateFlow<List<EjectedMassNetworkState>>(emptyList())
    private val _userProfiles = mutableMapOf<String, UserProfile>()
    private val _userProfileFlow = MutableStateFlow<UserProfile?>(null)
    private val _pendingResults = mutableMapOf<String, MutableList<SessionResult>>()
    private val _reports = mutableListOf<PlayerReportRecord>()
    private val _blocks = mutableMapOf<String, MutableMap<String, String>>()

    val submittedReports: List<PlayerReportRecord> get() = _reports.toList()

    private val _leaderboardFlow = MutableStateFlow<List<LeaderboardEntry>>(
        listOf(
            LeaderboardEntry("1", "Vortex", 1250),
            LeaderboardEntry("2", "Titan", 980),
            LeaderboardEntry("3", "Nebula", 740),
            LeaderboardEntry("4", "GloopMaster", 520)
        )
    )

    @OptIn(ExperimentalUuidApi::class)
    override suspend fun signInAnonymously(displayName: String): String {
        val uid = "${Uuid.random()}"
        currentUserId = uid
        registerOrUpdateUserProfile(uid, displayName, isAnonymous = true)
        return uid
    }

    override suspend fun getCurrentUserId(): String? = currentUserId

    override suspend fun signOut() {
        currentUserId = null
        _userProfileFlow.value = null
    }

    override suspend fun clearUserData(userId: String): Result<Unit> {
        val targetUid = userId.ifBlank { currentUserId.orEmpty() }
        if (targetUid.isEmpty()) {
            return Result.failure(IllegalStateException("No active user ID to clear"))
        }
        _userProfiles.remove(targetUid)
        if (_userProfileFlow.value?.id == targetUid) {
            _userProfileFlow.value = null
        }
        _leaderboardFlow.value = _leaderboardFlow.value.filterNot { it.id == targetUid }
        _playersFlow.value = _playersFlow.value.filterNot { it.id == targetUid }
        _pendingResults.remove(targetUid)
        _blocks.remove(targetUid)
        delay(1000L.milliseconds)
        signOut()
        return Result.success(Unit)
    }

    override suspend fun registerOrUpdateUserProfile(playerId: String, playerName: String, isAnonymous: Boolean) {
        val existing = _userProfiles[playerId]
        val resolvedName = if (playerName.isNotEmpty()) playerName else existing?.name ?: "Player"
        val updated = existing?.copy(name = resolvedName, isAnonymous = isAnonymous)
            ?: UserProfile(
                id = playerId,
                name = resolvedName,
                joinedAt = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble(),
                isAnonymous = isAnonymous
            )
        _userProfiles[playerId] = updated
        if (currentUserId == playerId) {
            _userProfileFlow.value = updated
        }
    }

    override suspend fun recordGameJoined(playerId: String, playerName: String, isSinglePlayer: Boolean) {
        val existing = _userProfiles[playerId]
        val resolvedName = if (playerName.isNotEmpty()) playerName else existing?.name ?: "Player"
        val updated = if (existing != null) {
            if (isSinglePlayer) {
                existing.copy(
                    name = resolvedName,
                    spGamesPlayed = existing.spGamesPlayed + 1
                )
            } else {
                existing.copy(
                    name = resolvedName,
                    gamesPlayed = existing.gamesPlayed + 1
                )
            }
        } else {
            UserProfile(
                id = playerId,
                name = resolvedName,
                joinedAt = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble(),
                gamesPlayed = if (isSinglePlayer) 0 else 1,
                spGamesPlayed = if (isSinglePlayer) 1 else 0,
                isAnonymous = true
            )
        }
        _userProfiles[playerId] = updated
        if (currentUserId == playerId) {
            _userProfileFlow.value = updated
        }
    }

    override fun observeUserProfile(playerId: String): Flow<UserProfile?> {
        return _userProfileFlow.asStateFlow()
    }

    override suspend fun recordGameStats(playerId: String, finalMass: Int, killedPlayer: Boolean, isSinglePlayer: Boolean) {
        val profile = _userProfiles[playerId] ?: return
        val updated = if (isSinglePlayer) {
            profile.copy(
                spBestMass = if (finalMass > 0) maxOf(profile.spBestMass, finalMass) else profile.spBestMass,
                spTotalKills = profile.spTotalKills + (if (killedPlayer) 1 else 0)
            )
        } else {
            profile.copy(
                bestMass = if (finalMass > 0) maxOf(profile.bestMass, finalMass) else profile.bestMass,
                totalKills = profile.totalKills + (if (killedPlayer) 1 else 0)
            )
        }
        _userProfiles[playerId] = updated
        if (currentUserId == playerId) {
            _userProfileFlow.value = updated
        }
    }

    override suspend fun submitScore(playerName: String, score: Int, userId: String) {
        val currentList = _leaderboardFlow.value.toMutableList()
        val playerKey = userId.ifEmpty { playerName }
        val existingIndex = currentList.indexOfFirst {
            (userId.isNotEmpty() && it.id == userId) || it.name == playerName
        }

        if (existingIndex >= 0) {
            val existing = currentList[existingIndex]
            if (score > existing.score) {
                currentList[existingIndex] = existing.copy(score = score, name = playerName)
            }
        } else {
            currentList.add(LeaderboardEntry(id = playerKey, name = playerName, score = score))
        }

        _leaderboardFlow.value = currentList
            .groupBy { if (it.id.isNotEmpty()) it.id else it.name }
            .map { (_, entries) -> entries.maxBy { it.score } }
            .sortedByDescending { it.score }
            .take(10)
    }

    override fun observeLeaderboard(limit: Int): Flow<List<LeaderboardEntry>> {
        return _leaderboardFlow.map { list ->
            list.groupBy { it.id.ifEmpty { it.name } }
                .map { (_, entries) -> entries.maxBy { it.score } }
                .sortedByDescending { it.score }
                .take(limit)
        }
    }

    override suspend fun submitPlayerReport(
        reporterUid: String,
        reportedUid: String,
        reportedNickname: String,
        reason: String
    ): Result<Unit> {
        return try {
            val nickname = requireValidPlayerReport(reporterUid, reportedUid, reportedNickname, reason)
            if (currentUserId != reporterUid) {
                return Result.failure(IllegalStateException("Reporter is not the signed-in user"))
            }
            _reports += PlayerReportRecord(
                reporterUid = reporterUid,
                reportedUid = reportedUid,
                reportedNickname = nickname,
                reason = reason
            )
            Result.success(Unit)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun setPlayerBlocked(
        ownerUid: String,
        blockedUid: String,
        nickname: String,
        blocked: Boolean
    ): Result<Unit> {
        if (ownerUid.isBlank() || blockedUid.isBlank() || ownerUid == blockedUid) {
            return Result.failure(IllegalArgumentException("Invalid block"))
        }
        val mine = _blocks.getOrPut(ownerUid) { mutableMapOf() }
        if (blocked) {
            val trimmed = nickname.trim()
            if (trimmed.isEmpty() || trimmed.length > 32) {
                return Result.failure(IllegalArgumentException("Invalid nickname"))
            }
            mine[blockedUid] = trimmed
        } else {
            mine.remove(blockedUid)
        }
        return Result.success(Unit)
    }

    override fun observeWorldPlayers(): Flow<List<PlayerNetworkState>> = _playersFlow.asStateFlow()

    override fun observeWorldPellets(): Flow<List<PelletNetworkState>> = _pelletsFlow.asStateFlow()

    override fun observeWorldEjected(): Flow<List<EjectedMassNetworkState>> = _ejectedFlow.asStateFlow()

    override suspend fun broadcastPlayerState(player: GloopPlayer, forceHeartbeat: Boolean) {
        val networkState = PlayerNetworkState(
            id = player.id,
            name = player.name,
            color = player.color.toString(),
            emoji = player.emoji,
            cells = player.cells.map { CellNetworkState(it.id, it.position.x, it.position.y, it.mass) },
            isAlive = player.isAlive,
            lastTimestamp = kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble(),
            joinedAt = player.joinedAt.toDouble()
        )
        val current = _playersFlow.value.filter { it.id != player.id }.toMutableList()
        if (player.isAlive) {
            current.add(networkState)
        }
        _playersFlow.value = current
    }

    override suspend fun spawnPellet(pellet: FoodPellet) {
        val list = _pelletsFlow.value.toMutableList()
        list.add(PelletNetworkState(pellet.id, pellet.position.x, pellet.position.y, pellet.color.toString(), pellet.mass, pellet.emoji))
        _pelletsFlow.value = list
    }

    override suspend fun spawnPellets(pellets: List<FoodPellet>) {
        if (pellets.isEmpty()) return
        val list = _pelletsFlow.value.toMutableList()
        pellets.forEach { pellet ->
            list.add(PelletNetworkState(pellet.id, pellet.position.x, pellet.position.y, pellet.color.toString(), pellet.mass, pellet.emoji))
        }
        _pelletsFlow.value = list
    }

    override suspend fun removePellet(pelletId: String) {
        _pelletsFlow.value = _pelletsFlow.value.filter { it.id != pelletId }
    }

    override suspend fun broadcastEjectedMass(mass: EjectedMass) {
        val list = _ejectedFlow.value.toMutableList()
        list.add(
            EjectedMassNetworkState(
                id = mass.id,
                ownerId = mass.ownerId,
                x = mass.position.x,
                y = mass.position.y,
                vx = mass.velocity.x,
                vy = mass.velocity.y,
                color = mass.color.toString(),
                mass = mass.mass,
                createdAt = if (mass.createdAt > 0L) mass.createdAt.toDouble() else kotlin.time.Clock.System.now().toEpochMilliseconds().toDouble()
            )
        )
        _ejectedFlow.value = list
    }

    override suspend fun removeEjectedMass(massId: String) {
        _ejectedFlow.value = _ejectedFlow.value.filter { it.id != massId }
    }

    override suspend fun consumePlayerCell(
        eaterPlayerId: String,
        eaterCellId: String,
        victimPlayerId: String,
        victimCellId: String,
        isLastCell: Boolean
    ) {
        if (isLastCell) {
            pruneStalePlayer(victimPlayerId)
        }
    }

    override suspend fun armDisconnectHandler(playerId: String, sessionId: String, approxMass: Float) {
        // In local mode, store pending disconnect result
        _pendingResults.getOrPut(playerId) { mutableListOf() }
    }

    override suspend fun cancelDisconnectHandler(playerId: String) {
        // No-op
    }

    override suspend fun pruneStalePlayer(stalePlayerId: String) {
        _playersFlow.value = _playersFlow.value.filter { it.id != stalePlayerId }
    }

    override suspend fun pruneStaleEjectedMasses(staleIds: List<String>) {
        val staleSet = staleIds.toSet()
        _ejectedFlow.value = _ejectedFlow.value.filter { it.id !in staleSet }
    }

    override suspend fun drainPendingSessionResults(playerId: String): List<SessionResult> {
        return _pendingResults.remove(playerId) ?: emptyList()
    }

    override fun goOnline() {}

    override fun goOffline() {}

    override suspend fun leaveWorld(playerId: String) {
        pruneStalePlayer(playerId)
    }
}
